import { createIdSlice } from '../utils/action/createElementSlice';
export const isSearchHint = (object) => {
    return (object === null || object === void 0 ? void 0 : object.type) === 'hint';
};
export const isSearchResult = (object) => {
    return (object === null || object === void 0 ? void 0 : object.type) === 'result';
};
export const isGeographicFeature = (object) => {
    return ((object === null || object === void 0 ? void 0 : object.type) === 'route' ||
        (object === null || object === void 0 ? void 0 : object.type) === 'pin' ||
        (object === null || object === void 0 ? void 0 : object.type) === 'area' ||
        (object === null || object === void 0 ? void 0 : object.type) === 'myLocationPin');
};
export const isGeographicRoute = (object) => {
    return (object === null || object === void 0 ? void 0 : object.type) === 'route';
};
export const isValidLocation = (latLonArr) => {
    var _a;
    return (_a = latLonArr === null || latLonArr === void 0 ? void 0 : latLonArr.every((value) => !isNaN(value))) !== null && _a !== void 0 ? _a : false;
};
export const isValidRoute = (routeLocations) => {
    return routeLocations.every((routeLocation) => {
        if (!(typeof routeLocation === 'number')) {
            return routeLocation.location !== undefined && routeLocation.location.length > 0;
        }
        return true;
    });
};
/**
 * Updates the state for a given set of items
 */
const updateItemStates = (state, updateProps) => {
    var _a, _b, _c;
    const { indices, property, value } = updateProps;
    if (!indices)
        return;
    const _objectSpread = (item, index, types) => {
        const typeSpecificIndices = indices
            .filter((index) => types.includes(index.type))
            .map((index) => index.index);
        if (typeSpecificIndices.includes(index)) {
            return Object.assign(Object.assign({}, item), { [property]: value });
        }
        return item;
    };
    return Object.assign(Object.assign({}, state), { searchHints: (_a = state.searchHints) === null || _a === void 0 ? void 0 : _a.map((item, index) => _objectSpread(item, index, ['hint'])), searchResults: (_b = state.searchResults) === null || _b === void 0 ? void 0 : _b.map((item, index) => _objectSpread(item, index, ['result'])), features: (_c = state.features) === null || _c === void 0 ? void 0 : _c.map((item, index) => _objectSpread(item, index, ['pin', 'route', 'area', 'myLocationPin'])) });
};
export const ObjectSearchState = createIdSlice({
    elementName: 'object-search',
    initialState: {},
    reducers: {
        /**
         * Sets ObjectSearchData. Setting undefined deletes any existing state.
         */
        set: (_state, action) => {
            return action.payload.data;
        },
        /**
         * Partial state update
         */
        update: (state, action) => {
            var _a;
            let pendingRedrawState = {};
            const { navSelection } = action.payload.data;
            if ('navSelection' in action.payload.data) {
                // check for any features which have pending changes to be drawn before navigating to the main search list
                const featureIndexPendingRedraw = (_a = state === null || state === void 0 ? void 0 : state.features) === null || _a === void 0 ? void 0 : _a.findIndex((feature) => {
                    // geographic area with valid location but not rendered yet
                    if ((feature === null || feature === void 0 ? void 0 : feature.type) === 'area' &&
                        isValidLocation(feature.latLon) &&
                        feature.isRendered === false) {
                        return true;
                    }
                    // geographic route with valid waypoint locations but not rendered yet
                    if ((feature === null || feature === void 0 ? void 0 : feature.type) === 'route' &&
                        feature.routeLocations.every((routeLocation) => {
                            if (!(typeof routeLocation === 'number')) {
                                return isValidLocation(routeLocation.latLon);
                            }
                            return false;
                        }) &&
                        feature.isRendered === false) {
                        return true;
                    }
                    return false;
                });
                if (featureIndexPendingRedraw !== undefined && featureIndexPendingRedraw !== -1) {
                    pendingRedrawState = {
                        featurePendingRedraw: {
                            index: featureIndexPendingRedraw,
                            pendingNavSelection: navSelection,
                        },
                        // we have a pending redraw - so stay where we are
                        navSelection: state === null || state === void 0 ? void 0 : state.navSelection,
                    };
                }
            }
            return Object.assign(Object.assign(Object.assign({}, state), action.payload.data), pendingRedrawState);
        },
        /**
         * Toggles the pinned state
         */
        setPinned: (state, action) => {
            if (!state)
                return;
            return Object.assign(Object.assign({}, state), { pinned: action.payload.data });
        },
        /**
         * Toggles pin mode
         */
        setPinModeEnabled: (state, action) => {
            if (!state)
                return;
            return Object.assign(Object.assign({}, state), { pinModeEnabled: action.payload.data });
        },
        /**
         * Enables/Disables the auto linkage between geographic features and pins
         */
        toggleAutoLinkToPins: (state, _action) => {
            if (!state)
                return;
            return Object.assign(Object.assign({}, state), { autoLinkToPins: !state.autoLinkToPins });
        },
        /**
         * Enables/Disables the route arrows
         */
        toggleRouteArrows: (state, _action) => {
            if (!state)
                return;
            return Object.assign(Object.assign({}, state), { routeArrows: !state.routeArrows });
        },
        /**
         * Sets the type of the route arrows
         */
        setRouteArrowType: (state, action) => {
            if (!state)
                return;
            const arrowType = action.payload.data;
            return Object.assign(Object.assign({}, state), { routeArrowType: arrowType });
        },
        notifyRefreshLocation: (state, _action) => {
            // TODO: refresh My Location when this action is called
            return state;
        },
        notifyNewPin: (state, _action) => {
            return state;
        },
        notifyUpdateSearchResult: (state, _action) => {
            return state;
        },
        notifyUpdatePin: (state, _action) => {
            return state;
        },
        notifyUpdateRoute: (state, _action) => {
            return state;
        },
        notifyUpdateArea: (state, _action) => {
            return state;
        },
        notifyDeletePins: (state, _action) => {
            return state;
        },
        notifyDeleteAllPins: (state, _action) => {
            return state;
        },
        notifyUpdateHints: (state, _action) => {
            return state;
        },
        notifyApplyHint: (state, _action) => {
            return state;
        },
        notifySearch: (state, _action) => {
            return state;
        },
        notifyDeleteSearchResults: (state, _action) => {
            return state;
        },
        notifyConvertSearchResults: (state, _action) => {
            return state;
        },
        notifyConvertAllSearchResults: (state, _action) => {
            return state;
        },
        notifyDrawAll: (state, _action) => {
            return state;
        },
        notifyNewRoute: (state, _action) => {
            return state;
        },
        notifyDrawRoute: (state, _action) => {
            return state;
        },
        notifyClearRoute: (state, _action) => {
            return state;
        },
        notifyNewArea: (state, _action) => {
            return state;
        },
        notifyDrawArea: (state, _action) => {
            return state;
        },
        notifyClearArea: (state, _action) => {
            return state;
        },
        /**
         * Updates a state property for a given list of items (ids)
         * @deprecated property updates should be "notify" actions handled by portable
         */
        updateItemStates: (state, action) => {
            if (!state)
                return;
            return updateItemStates(state, action.payload.data);
        },
        notifyUpdateSelection: (state, _action) => {
            return state;
        },
        notifyVisibilityChange: (state, _action) => {
            return state;
        },
        notifyOpen: (state) => {
            return state;
        },
        /**
         * Updates the country selection state. This could be triggered by the user opening the
         * {DemographicsSelectorDialog} for a given item or changing the country within the dialog.
         */
        setSelectedDemographicCountry: (state, action) => {
            if (!state || !state.features)
                return;
            const { index, country } = action.payload.data;
            return Object.assign(Object.assign({}, state), { features: state.features.map((item, itemIndex) => {
                    return itemIndex === index && item.type === 'area'
                        ? Object.assign(Object.assign({}, item), { demographics: Object.assign(Object.assign({}, item.demographics), { selectedCountry: country }) }) : item;
                }) });
        },
        /**
         * Updates the selected measures for a given feature
         */
        setSelectedDemographicMeasures: (state, action) => {
            if (!state || !state.features)
                return;
            const { index, selectedMeasures } = action.payload.data;
            return Object.assign(Object.assign({}, state), { features: state.features.map((item, itemIndex) => {
                    var _a;
                    if (itemIndex === index && item.type === 'area') {
                        let recentMeasures = ((_a = item.demographics) === null || _a === void 0 ? void 0 : _a.recentMeasures) || [];
                        selectedMeasures.forEach((measure) => {
                            // remove from recent if existing
                            recentMeasures = recentMeasures.filter((e) => e !== measure);
                            // add back to top position
                            recentMeasures.unshift(measure);
                        });
                        return Object.assign(Object.assign({}, item), { demographics: Object.assign(Object.assign({}, item.demographics), { selectedMeasures: selectedMeasures, recentMeasures: recentMeasures }) });
                    }
                    return item;
                }) });
        },
        /**
         * Updates the demographic data for the given list of variables
         */
        setDemographicData: (state, action) => {
            if (!state || !state.features)
                return;
            const { index, data } = action.payload.data;
            return Object.assign(Object.assign({}, state), { features: state.features.map((item, itemIndex) => {
                    return Object.assign(Object.assign({}, item), (itemIndex === index &&
                        item.type === 'area' && {
                        demographics: Object.assign(Object.assign({}, item.demographics), { data: data }),
                    }));
                }) });
        },
    },
});
